import React, { useEffect, useState } from "react";
import { IoMdClose } from "react-icons/io";
import { RiErrorWarningLine } from "react-icons/ri";
import { useDispatch } from "react-redux";
import { setInitial } from "../../store/slices/chatSlice";
import { useLang } from "../../context/LanguageContext";
import ContactPlanService from "../../services/contact-plan";
import { toastError, toastSuccess } from "../../components/ToastNotification";
import { CustomPagination } from "../../components/Pagination";

const TRANSLATIONS = {
  EN: {
    title: "Contact Plans",
    subtitle: "Review and approve contact us plan requests",
    colId: "Id",
    colName: "Name",
    colEmail: "Email",
    colPhone: "Phone",
    colNote: "Note",
    colStatus: "Status",
    colActions: "Actions",
    approve: "Approve",
    reject: "Reject",
    noData: "No contact plan requests found",
    noDataHint: "Requests submitted by users will appear here",
    modalTitle: "Review Request",
    labelContactId: "Contact ID",
    labelStartDate: "Start Date",
    labelEndDate: "End Date",
    labelToken: "Token Limit",
    labelDecision: "Decision",
    approveBtn: "Approve",
    rejectBtn: "Reject",
    cancel: "Cancel",
    submit: "Submit",
    statusApproved: "Approved",
    statusRejected: "Rejected",
    toastApproved: "Request approved successfully!",
    toastRejected: "Request rejected successfully!",
    errStartDate: "Start date is required",
    errEndDate: "End date is required",
    errToken: "Token limit is required",
    errRejectReason: "Please provide a reason for rejection",
    confirmRejectTitle: "Reject Request",
    confirmRejectMsg: "Are you sure you want to reject this request? This action cannot be undone.",
    confirmRejectBtn: "Yes, Reject",
    confirmCancel: "Cancel",
    rejectReasonLabel: "Rejection Reason",
    rejectReasonPlaceholder: "Please provide a reason for rejecting this request...",
  },
  ES: {
    title: "Planes de Contacto",
    subtitle: "Revisar y aprobar solicitudes de planes de contacto",
    colId: "Id",
    colName: "Nombre",
    colEmail: "Correo",
    colPhone: "Teléfono",
    colNote: "Nota",
    colStatus: "Estado",
    colActions: "Acciones",
    approve: "Aprobar",
    reject: "Rechazar",
    noData: "No se encontraron solicitudes de plan",
    noDataHint: "Las solicitudes enviadas por los usuarios aparecerán aquí",
    modalTitle: "Revisar Solicitud",
    labelContactId: "ID de Contacto",
    labelStartDate: "Fecha de Inicio",
    labelEndDate: "Fecha de Fin",
    labelToken: "Límite de Tokens",
    labelDecision: "Decisión",
    approveBtn: "Aprobar",
    rejectBtn: "Rechazar",
    cancel: "Cancelar",
    submit: "Enviar",
    toastApproved: "¡Solicitud aprobada exitosamente!",
    toastRejected: "¡Solicitud rechazada exitosamente!",
    errStartDate: "La fecha de inicio es requerida",
    errEndDate: "La fecha de fin es requerida",
    errToken: "El límite de tokens es requerido",
    errRejectReason: "Por favor, proporcione una razón para el rechazo",
    confirmRejectTitle: "Rechazar Solicitud",
    confirmRejectMsg: "¿Estás seguro de rechazar esta solicitud? Esta acción no se puede deshacer.",
    confirmRejectBtn: "Sí, Rechazar",
    confirmCancel: "Cancelar",
    rejectReasonLabel: "Motivo del Rechazo",
    rejectReasonPlaceholder: "Por favor, proporcione una razón para rechazar esta solicitud...",
  },
};

const PAGE_SIZES = [10, 20, 30, 50];

const ContactPlan = () => {
  const dispatch = useDispatch();
  const { lang } = useLang();
  const t = TRANSLATIONS[lang];

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // Approve modal state
  const [approveModal, setApproveModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [form, setForm] = useState({ startDate: "", endDate: "", token: "" });
  const [formErrors, setFormErrors] = useState({});

  // Reject confirm modal state
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectRecord, setRejectRecord] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectReasonError, setRejectReasonError] = useState("");

  useEffect(() => {
    dispatch(setInitial(true));
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [page, pageSize, search]);

  const fetchRecords = async () => {
    try {
      if (!records || records.length === 0) setLoading(true);
      const res = await ContactPlanService.getAll({ page, limit: pageSize, search });
      if (res?.data) {
        setRecords(res.data.data || []);
        setTotalCount(res.data.totalCount || 0);
      }
    } catch (err) {
      toastError(err?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (type, p) => {
    const totalPages = Math.ceil(totalCount / pageSize);
    if (type === "Next")     setPage(prev => Math.min(prev + 1, totalPages));
    else if (type === "Previous") setPage(prev => Math.max(prev - 1, 1));
    else if (type === "First")    setPage(1);
    else if (type === "Last")     setPage(totalPages);
    else                          setPage(p);
  };

  const handleDataPerPage = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  // Approve flow
  const openApprove = (record) => {
    setSelectedRecord(record);
    const today = new Date().toISOString().split("T")[0];
    setForm({ startDate: today, endDate: "", token: "" });
    setFormErrors({});
    setApproveModal(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!form.startDate) errors.startDate = t.errStartDate;
    if (!form.endDate)   errors.endDate   = t.errEndDate;
    if (!form.token)     errors.token     = t.errToken;
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleApproveSubmit = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      // Using the API response format provided
      await ContactPlanService.approve({
        contactId:  selectedRecord._id,
        isApprove:  true,
        startDate:  form.startDate,
        endDate:    form.endDate,
        token:      Number(form.token),
      });
      toastSuccess(t.toastApproved);
      setApproveModal(false);
      fetchRecords();
    } catch (err) {
      toastError(err?.response?.data?.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Reject flow with reason
  const openReject = (record) => {
    setRejectRecord(record);
    setRejectReason("");
    setRejectReasonError("");
    setRejectModal(true);
  };

  const handleRejectConfirm = async () => {
    // Validate reason
    if (!rejectReason.trim()) {
      setRejectReasonError(t.errRejectReason);
      return;
    }
    
    setSubmitting(true);
    try {
      // Using the API response format with isApprove: false and reason
      await ContactPlanService.approve({
        contactId: rejectRecord._id,
        isApprove: false,
        note: rejectReason.trim(), // Send the rejection reason in the note field
        startDate: "",
        endDate: "",
        token: 0
      });
      toastSuccess(t.toastRejected);
      setRejectModal(false);
      setRejectReason("");
      setRejectReasonError("");
      fetchRecords();
    } catch (err) {
      toastError(err?.response?.data?.message);
    } finally {
      setSubmitting(false);
    }
  };

  const statusLabel = (s) => {

    return t.statusPending;
  };

  return (
    <div className="flex flex-col w-full h-full overflow-hidden">
      <div className="flex flex-col flex-1 min-h-0 p-4 lg:p-6 gap-4 overflow-hidden">

        {/* Header */}
        <div className="flex-shrink-0">
          <h1 className="font-roboto font-bold text-xl lg:text-2xl text-primary uppercase tracking-wide">
            {t.title}
          </h1>
          <p className="font-roboto text-sm text-gray-400 mt-0.5">{t.subtitle}</p>
        </div>

        {/* Search bar - commented out as per original */}
        {/* <form onSubmit={handleSearchSubmit} className="flex gap-2 flex-shrink-0">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={lang === "ES" ? "Buscar por nombre o correo..." : "Search by name or email..."}
            className="flex-1 max-w-xs px-4 py-2 rounded-xl border border-primary/20 bg-[#F0EEF8]/50 text-primary font-roboto text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-gray-400"
          />
          <button
            type="submit"
            className="px-5 py-2 rounded-xl bg-primary text-white font-roboto font-bold text-sm uppercase tracking-wide hover:bg-primary/90 transition-all"
          >
            {lang === "ES" ? "Buscar" : "Search"}
          </button>
        </form> */}

        {/* Table area */}
        <div className="flex flex-col flex-1 min-h-0 bg-white rounded-2xl border border-primary/10 shadow-sm overflow-hidden">
          {loading && (!records || records.length === 0) ? (
            <ContactPlanTableSkeleton t={t} pageSize={pageSize} />
          ) : records.length === 0 ? (
            <div className="flex flex-col justify-center items-center flex-1 gap-2 text-center px-6">
              <RiErrorWarningLine className="text-4xl text-primary/40" />
              <p className="font-roboto font-semibold text-primary">{t.noData}</p>
              <p className="font-roboto text-sm text-primary/60">{t.noDataHint}</p>
            </div>
          ) : (
            <>
              {/* Scrollable table */}
              <div className="flex-1 overflow-auto min-h-0">
                <table className="w-full min-w-[640px] border-collapse">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-primary">
                      {[t.colId, t.colName, t.colEmail, t.colPhone, t.colNote , t.colActions].map((h) => (
                        <th
                          key={h}
                          className="px-4 py-3 text-left font-roboto font-bold text-xs text-white uppercase tracking-wide whitespace-nowrap"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((r, idx) => (
                      <tr
                        key={r._id}
                        className={`border-b border-gray-100 transition-colors hover:bg-[#F0EEF8]/30 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50/40"}`}
                      >
                        <td className="px-4 py-3 font-roboto text-sm text-gray-500 whitespace-nowrap">
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className="px-4 py-3 font-roboto text-sm text-gray-800 font-medium whitespace-nowrap">
                          {r.name || r.userId?.email || "—"}
                        </td>
                        <td className="px-4 py-3 font-roboto text-sm text-gray-600 whitespace-nowrap">
                          {r.email || r.userId?.email || "—"}
                        </td>
                        <td className="px-4 py-3 font-roboto text-sm text-gray-600 whitespace-nowrap">
                          {[r.country_code, r.phone_no].filter(Boolean).join(" ") || "—"}
                        </td>
                        <td className="px-4 py-3 font-roboto text-sm text-gray-500 max-w-[200px] truncate" title={r.note}>
                          {r.note || "—"}
                        </td>
                    
                        <td className="px-4 py-3">
                          {r.status === "pending" ? (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openApprove(r)}
                                className="px-3 py-1.5 rounded-lg font-roboto font-semibold text-xs text-white bg-primary hover:bg-primary/90 transition-all uppercase tracking-wide whitespace-nowrap"
                              >
                                {t.approve}
                              </button>
                              <button
                                onClick={() => openReject(r)}
                                className="px-3 py-1.5 rounded-lg font-roboto font-semibold text-xs text-red-600 border border-red-200 hover:bg-red-50 transition-all uppercase tracking-wide whitespace-nowrap"
                              >
                                {t.reject}
                              </button>
                            </div>
                          ) : (
                            <span className="font-roboto text-xs text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex-shrink-0 border-t border-gray-100 px-2">
                <CustomPagination
                  total={totalCount}
                  page={page}
                  handleChange={handleChange}
                  dataPerPage={pageSize}
                  handleDataPerPage={handleDataPerPage}
                  PAGE_SIZES={PAGE_SIZES}
                  lang={lang}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Approve Modal ─────────────────────────────────────────── */}
      {approveModal && selectedRecord && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
              <h2 className="font-roboto font-bold text-lg text-primary uppercase tracking-wide">
                {t.modalTitle}
              </h2>
              <button
                onClick={() => setApproveModal(false)}
                className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-[#F0EEF8] text-gray-400 hover:text-primary transition-colors"
              >
                <IoMdClose className="text-lg" />
              </button>
            </div>

            <div className="px-6 py-5 flex flex-col gap-4">
              {/* Read-only info */}
              <div className="bg-[#F0EEF8]/50 rounded-xl px-4 py-3 flex flex-col gap-1">
                <p className="font-roboto text-xs text-gray-500 uppercase tracking-wide">{t.colName}</p>
                <p className="font-roboto text-sm font-semibold text-gray-800">{selectedRecord.name || selectedRecord.userId?.email || "—"}</p>
                <p className="font-roboto text-xs text-gray-500 mt-1">{selectedRecord.email || selectedRecord.userId?.email}</p>
                {selectedRecord.note && (
                  <p className="font-roboto text-xs text-gray-400 mt-1 italic">"{selectedRecord.note}"</p>
                )}
              </div>

              {/* Start Date */}
              <div>
                <label className="font-roboto text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                  {t.labelStartDate} <span className="text-red-400">*</span>
                </label>
                <input
                  type="date"
                  value={form.startDate}
                  onChange={(e) => setForm(p => ({ ...p, startDate: e.target.value }))}
                  min={new Date().toISOString().split("T")[0]}
                  className="w-full px-4 py-2.5 rounded-xl border border-primary/20 font-roboto text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white"
                />
                {formErrors.startDate && (
                  <p className="font-roboto text-xs text-red-500 mt-1">{formErrors.startDate}</p>
                )}
              </div>

              {/* End Date */}
              <div>
                <label className="font-roboto text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                  {t.labelEndDate} <span className="text-red-400">*</span>
                </label>
                <input
                  type="date"
                  value={form.endDate}
                  onChange={(e) => setForm(p => ({ ...p, endDate: e.target.value }))}
                  min={new Date().toISOString().split("T")[0]}
                  className="w-full px-4 py-2.5 rounded-xl border border-primary/20 font-roboto text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white"
                />
                {formErrors.endDate && (
                  <p className="font-roboto text-xs text-red-500 mt-1">{formErrors.endDate}</p>
                )}
              </div>

              {/* Token */}
              <div>
                <label className="font-roboto text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                  {t.labelToken} <span className="text-red-400">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  value={form.token}
                  onChange={(e) => setForm(p => ({ ...p, token: e.target.value }))}
                  placeholder="e.g. 500000"
                  className="w-full px-4 py-2.5 rounded-xl border border-primary/20 font-roboto text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white"
                />
                {formErrors.token && (
                  <p className="font-roboto text-xs text-red-500 mt-1">{formErrors.token}</p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 pb-6">
              <button
                onClick={() => setApproveModal(false)}
                className="px-5 py-2.5 rounded-xl font-roboto font-semibold text-sm text-gray-600 border border-gray-200 hover:bg-gray-50 transition-all"
              >
                {t.cancel}
              </button>
              <button
                onClick={handleApproveSubmit}
                disabled={submitting}
                className="px-6 py-2.5 rounded-xl font-roboto font-bold text-sm text-white bg-primary hover:bg-primary/90 transition-all uppercase tracking-wide disabled:opacity-60 min-w-[100px] flex items-center justify-center"
              >
                {submitting ? <div className="loader" /> : t.approveBtn}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject Confirm Modal with Reason ──────────────────────── */}
      {rejectModal && rejectRecord && (
        <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
              <h2 className="font-roboto font-bold text-lg text-red-500 uppercase tracking-wide">
                {t.confirmRejectTitle}
              </h2>
              <button
                onClick={() => {
                  setRejectModal(false);
                  setRejectReason("");
                  setRejectReasonError("");
                }}
                className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
              >
                <IoMdClose className="text-lg" />
              </button>
            </div>
            
            <div className="px-6 py-6 flex flex-col gap-4">
              <div className="flex flex-col items-center gap-2 text-center">
                <RiErrorWarningLine className="text-5xl text-red-400" />
                <p className="font-roboto text-sm text-gray-600">{t.confirmRejectMsg}</p>
                <p className="font-roboto text-sm font-semibold text-gray-800">
                  {rejectRecord.name || rejectRecord.email || rejectRecord.userId?.email}
                </p>
              </div>

              {/* Rejection Reason Input */}
              <div className="mt-2">
                <label className="font-roboto text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                  {t.rejectReasonLabel} <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => {
                    setRejectReason(e.target.value);
                    if (e.target.value.trim()) {
                      setRejectReasonError("");
                    }
                  }}
                  placeholder={t.rejectReasonPlaceholder}
                  rows="4"
                  className={`w-full px-4 py-2.5 rounded-xl border font-roboto text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-red-300 bg-white transition-all ${
                    rejectReasonError ? 'border-red-400 ring-1 ring-red-400' : 'border-gray-200'
                  }`}
                />
                {rejectReasonError && (
                  <p className="font-roboto text-xs text-red-500 mt-1 flex items-center gap-1">
                    <span className="inline-block w-1 h-1 rounded-full bg-red-500"></span>
                    {rejectReasonError}
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 pb-6">
              <button
                onClick={() => {
                  setRejectModal(false);
                  setRejectReason("");
                  setRejectReasonError("");
                }}
                className="px-5 py-2.5 rounded-xl font-roboto font-semibold text-sm text-gray-600 border border-gray-200 hover:bg-gray-50 transition-all"
              >
                {t.confirmCancel}
              </button>
              <button
                onClick={handleRejectConfirm}
                disabled={submitting}
                className="px-6 py-2.5 rounded-xl font-roboto font-bold text-sm text-white bg-red-500 hover:bg-red-600 transition-all uppercase tracking-wide disabled:opacity-60 min-w-[100px] flex items-center justify-center"
              >
                {submitting ? <div className="loader" /> : t.confirmRejectBtn}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Contact Plans Table Shimmer Skeleton ───────────────────────── */
const ContactPlanTableSkeleton = ({ t, pageSize = 10 }) => {
  const rowCount = Math.min(pageSize || 10, 8);
  return (
    <div className="flex flex-col flex-1 min-h-0 select-none">
      <div className="flex-1 overflow-auto min-h-0">
        <table className="w-full min-w-[640px] border-collapse">
          <thead className="sticky top-0 z-10">
            <tr className="bg-primary">
              {[t.colId, t.colName, t.colEmail, t.colPhone, t.colNote, t.colActions].map((h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-left font-roboto font-bold text-xs text-white uppercase tracking-wide whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {Array.from({ length: rowCount }).map((_, idx) => (
              <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50/40"}>
                <td className="px-4 py-3.5">
                  <div className="w-6 h-3.5 rounded skeleton-bone" />
                </td>
                <td className="px-4 py-3.5">
                  <div className="w-28 h-4 rounded skeleton-bone" />
                </td>
                <td className="px-4 py-3.5">
                  <div className="w-36 h-3.5 rounded skeleton-bone" />
                </td>
                <td className="px-4 py-3.5">
                  <div className="w-24 h-3.5 rounded skeleton-bone" />
                </td>
                <td className="px-4 py-3.5">
                  <div className="w-44 h-3.5 rounded skeleton-bone" />
                </td>
                <td className="px-4 py-3.5">
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-7 rounded-lg skeleton-bone-primary" />
                    <div className="w-16 h-7 rounded-lg skeleton-bone" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="border-t border-gray-100 p-3 flex justify-between items-center bg-gray-50/50 flex-shrink-0">
        <div className="w-32 h-4 rounded skeleton-bone" />
        <div className="w-48 h-7 rounded-xl skeleton-bone" />
      </div>
    </div>
  );
};

export default ContactPlan;